/**
 * `MediaStore` over the Origin Private File System.
 *
 * `appendToForm` reads the blob and hands it to `FormData`, which is the
 * browser's only option — and the reason the interface is shaped around the
 * append rather than around returning bytes. The native implementation of the
 * same method passes `{uri, name, type}` and never loads the file.
 */
import type { MediaData, MediaStore, SaveMediaOptions } from '@kapture/platform';
import {
  deleteMediaBlob,
  readMediaBlob,
  saveMediaBlob,
  totalMediaBytes,
} from '@/lib/storage/opfs';

/** Everything the browser can store arrives as, or becomes, a Blob. */
function toBlob(data: MediaData): Blob {
  if (data instanceof Blob) return data;
  if (data instanceof Uint8Array) return new Blob([data as BlobPart]);
  // A bare uri has no meaning here: OPFS is not addressable by URL, and the
  // browser capture path never produces one. Fail loudly rather than write an
  // empty file that surfaces later as a photo that vanished.
  throw new Error(
    'opfsMediaStore cannot store a { uri } source; the browser capture path produces a Blob',
  );
}

export const opfsMediaStore: MediaStore = {
  save(options: SaveMediaOptions): Promise<string> {
    return saveMediaBlob({
      sessionId: options.sessionId,
      captureId: options.captureId,
      extension: options.extension,
      blob: toBlob(options.data),
    });
  },
  async read(path: string): Promise<Uint8Array> {
    const blob = await readMediaBlob(path);
    return new Uint8Array(await blob.arrayBuffer());
  },
  delete: deleteMediaBlob,
  totalBytes: totalMediaBytes,
  async appendToForm(form: FormData, field: string, path: string, mime?: string): Promise<void> {
    const blob = await readMediaBlob(path);
    // Re-wrap with the mime the recorder declared. An OPFS `File`'s `.type`
    // is whatever the browser infers from the extension, and Chromium infers
    // `.webm` as `video/webm` — so a voice note went up announcing itself as
    // video, the server refused it 415, and 415 is terminal in the queue: the
    // recording died on the device rather than waiting for a better link.
    const part = mime ? new Blob([blob], { type: mime }) : blob;
    form.append(field, part, path.split('/').pop() ?? 'upload.bin');
  },
};
